// @ts-nocheck
// ============================================
// Events Slice - управление событиями
// ============================================

import { StateCreator } from 'zustand';
import { nanoid } from 'nanoid';
import { AnalyticsEvent, EventStatus, Comment } from '../../types';
import { createEvent, updateEvent as updateEventInFirestore, deleteEvent as deleteEventInFirestore } from '../../lib/firebase/projectService';
import { permissionService } from '../../services/permissionService';
import { markCommentAsRead } from '../../lib/firebase/readReceiptService';

const generateId = () => nanoid();

// Helper function for deep cloning events to ensure immutable updates
const deepCloneEvent = (event: AnalyticsEvent): AnalyticsEvent => {
  return {
    ...event,
    params: event.params ? JSON.parse(JSON.stringify(event.params)) : [],
    comments: event.comments ? JSON.parse(JSON.stringify(event.comments)) : [],
    previousValues: event.previousValues ? JSON.parse(JSON.stringify(event.previousValues)) : undefined
  };
};

export interface EventsState {
  // Events state
  selectedEvent: AnalyticsEvent | null;
  
  // Events actions
  addEvent: (screenId: string, eventData: Omit<AnalyticsEvent, 'id' | 'comments' | 'status'>) => void;
  updateEvent: (screenId: string, eventId: string, updates: Partial<AnalyticsEvent>) => void;
  deleteEvent: (screenId: string, eventId: string) => void;
  updateEventPosition: (screenId: string, eventId: string, x: number, y: number) => void;
  selectEvent: (eventId: string | null) => void;
  clearSelection: () => void;
  toggleEventImplemented: (screenId: string, eventId: string) => void;
  setEventStatus: (screenId: string, eventId: string, status: EventStatus) => boolean;
  confirmChanges: (screenId: string, eventId: string) => boolean;
  
  // Comments actions
  addComment: (screenId: string, eventId: string, text: string) => void;
  deleteComment: (screenId: string, eventId: string, commentId: string) => void;
  markCommentsAsRead: (screenId: string, eventId: string) => void;
}

export const createEventsSlice: StateCreator<EventsState, [], [], EventsState> = (set, get) => ({
  // Initial state
  selectedEvent: null,
  
  // Add new event
  addEvent: async (screenId: string, eventData: Omit<AnalyticsEvent, 'id' | 'comments' | 'status'>) => {
    console.log('[eventsSlice.addEvent] Called with:', { screenId, eventData });
    const { apps, currentApp, currentVersion, currentScreen } = (get() as any);
    console.log('[eventsSlice.addEvent] Current state:', {
      hasCurrentApp: !!currentApp,
      hasCurrentVersion: !!currentVersion,
      currentAppId: currentApp?.id,
      currentVersionId: currentVersion?.id,
      screenId
    });
    
    if (!currentApp || !currentVersion) {
      console.error('[eventsSlice.addEvent] ERROR: No currentApp or currentVersion');
      return;
    }
    
    const user = (get() as any).user;
    console.log('[eventsSlice.addEvent] User:', { id: user?.id, role: user?.role });
    
    if (!user) {
      console.error('[eventsSlice.addEvent] ERROR: No user');
      return;
    }
    
    const newEvent: AnalyticsEvent = {
      id: generateId(),
      ...eventData,
      comments: [],
      status: 'new'
    };
    
    // Сохраняем предыдущее состояние для rollback
    const previousApps = apps;
    const previousCurrentApp = currentApp;
    const previousCurrentVersion = currentVersion;
    const previousCurrentScreen = currentScreen;
    
    // Обновляем локальное состояние оптимистично
    const updatedVersions = (currentApp.versions || []).map((v: any) => {
      if (v.id === currentVersion.id) {
        return {
          ...v,
          screens: (v.screens || []).map((s: any) => {
            if (s.id === screenId) {
              return {
                ...s,
                events: [...(s.events || []).map((e: AnalyticsEvent) => deepCloneEvent(e)), newEvent]
              };
            }
            return s;
          })
        };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: any) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    let updatedCurrentScreen = currentScreen;
    if (currentScreen?.id === screenId) {
      updatedCurrentScreen = { ...currentScreen, events: [...(currentScreen.events || []), newEvent] };
    }
    
    const updatedCurrentVersion = updatedVersions.find((v: any) => v.id === currentVersion.id) || currentVersion;
    
    set({
      apps: updatedApps,
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion,
      currentScreen: updatedCurrentScreen,
      selectedEvent: newEvent
    });
    
    console.log('[eventsSlice.addEvent] ✅ Local state updated. Event:', newEvent.name, 'ID:', newEvent.id);
    
    // Add history record - only store relevant fields for history display
    (get() as any).addHistoryRecord({
      projectId: currentApp.id,
      action: 'create',
      entityType: 'event',
      entityId: newEvent.id,
      entityName: newEvent.name,
      userId: user.id,
      userName: user.displayName || user.name,
      newValue: { name: newEvent.name, type: newEvent.type, description: newEvent.description, params: newEvent.params },
      description: `Створено подію "${newEvent.name}" на екрані "${currentScreen?.displayName || currentScreen?.name || 'unknown'}"`
    });
    
    // Сохраняем в Firestore
    try {
      const result = await createEvent(
        currentApp.id,
        currentVersion.id,
        screenId,
        newEvent.id, // Используем существующий ID события
        eventData,
        user
      );
      
      // Update timestamp AFTER successful Firebase operation
      const { updateLastLocalUpdate } = (get() as any);
      if (updateLastLocalUpdate) updateLastLocalUpdate();
      
      if (!result) {
        // Rollback при ошибке
        console.log('[eventsSlice.addEvent] ❌ Rollback - Firebase error');
        set({
          apps: previousApps,
          currentApp: previousCurrentApp,
          currentVersion: previousCurrentVersion,
          currentScreen: previousCurrentScreen,
          selectedEvent: null
        });
        console.error('Failed to create event in Firestore');
      } else {
        console.log('[eventsSlice.addEvent] ✅ Firebase sync successful. Event ID:', result.id);
      }
    } catch (error) {
      // Rollback при ошибке
      set({
        apps: previousApps,
        currentApp: previousCurrentApp,
        currentVersion: previousCurrentVersion,
        currentScreen: previousCurrentScreen,
        selectedEvent: null
      });
      console.error('Error creating event in Firestore:', error);
    }
  },
  
  // Update event
  updateEvent: async (screenId: string, eventId: string, updates: Partial<AnalyticsEvent>) => {
    const { apps, currentApp, currentVersion, currentScreen, selectedEvent } = (get() as any);
    if (!currentApp || !currentVersion) return;
    
    const user = (get() as any).user;
    if (!user) return;
    
    const event = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
    
    // Check if this is a content change (not just position change)
    const isContentChange = updates.name !== undefined ||
                            updates.type !== undefined ||
                            updates.description !== undefined ||
                            updates.params !== undefined;
    
    const updateKeys = Object.keys(updates);
    const onlyPositionChange = updateKeys.length > 0 &&
                               updateKeys.every(key => ['x', 'y'].includes(key));
    
    // Set status to 'edited' when content is changed (for any status except already edited)
    if (isContentChange && !onlyPositionChange && event?.status !== 'edited') {
      // Build previousValues with ONLY the fields that actually changed
      const previousValues: any = {};
      if (updates.name !== undefined) {
        previousValues.name = event.name;
      }
      if (updates.type !== undefined) {
        previousValues.type = event.type;
      }
      if (updates.description !== undefined) {
        previousValues.description = event.description;
      }
      if (updates.params !== undefined) {
        previousValues.params = event.params;
      }
      // Only add change metadata if there are actual content changes
      if (Object.keys(previousValues).length > 0) {
        previousValues.changedAt = new Date().toISOString();
        previousValues.changedBy = user?.displayName || user?.name || 'unknown';
        previousValues.changedById = user?.id;
        updates = { ...updates, status: 'edited', previousValues };
      }
    }
    
    // Preserve previousValues for already edited events
    if (event?.status === 'edited' && event.previousValues) {
      if (isContentChange && !onlyPositionChange && !updates.previousValues) {
        updates = { ...updates, previousValues: event.previousValues };
      }
    }
    
    // Сохраняем предыдущее состояние для rollback
    const previousApps = apps;
    const previousCurrentApp = currentApp;
    const previousCurrentVersion = currentVersion;
    const previousCurrentScreen = currentScreen;
    const previousSelectedEvent = selectedEvent;
    
    const updatedVersions = (currentApp.versions || []).map((v: any) => {
      if (v.id === currentVersion.id) {
        return {
          ...v,
          screens: (v.screens || []).map((s: any) => {
            if (s.id === screenId) {
              return {
                ...s,
                events: (s.events || []).map((e: AnalyticsEvent) =>
                  e.id === eventId ? { ...deepCloneEvent(e), ...updates,
                    // Deep clone the updated nested fields
                    params: updates.params ? JSON.parse(JSON.stringify(updates.params)) : e.params,
                    comments: updates.comments ? JSON.parse(JSON.stringify(updates.comments)) : e.comments,
                    previousValues: updates.previousValues ? JSON.parse(JSON.stringify(updates.previousValues)) : e.previousValues
                  } : deepCloneEvent(e)
                )
              };
            }
            return s;
          })
        };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: any) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    let updatedCurrentScreen = currentScreen;
    let updatedSelectedEvent = selectedEvent;
    
    if (currentScreen?.id === screenId) {
      updatedCurrentScreen = {
        ...currentScreen,
        events: (currentScreen.events || []).map((e: AnalyticsEvent) =>
          e.id === eventId ? {
            ...deepCloneEvent(e),
            ...updates,
            // Deep clone the updated nested fields
            params: updates.params ? JSON.parse(JSON.stringify(updates.params)) : e.params,
            comments: updates.comments ? JSON.parse(JSON.stringify(updates.comments)) : e.comments,
            previousValues: updates.previousValues ? JSON.parse(JSON.stringify(updates.previousValues)) : e.previousValues
          } : deepCloneEvent(e)
        )
      };
    }
    
    if (selectedEvent?.id === eventId) {
      updatedSelectedEvent = {
        ...deepCloneEvent(selectedEvent),
        ...updates,
        // Deep clone the updated nested fields
        params: updates.params ? JSON.parse(JSON.stringify(updates.params)) : selectedEvent.params,
        comments: updates.comments ? JSON.parse(JSON.stringify(updates.comments)) : selectedEvent.comments,
        previousValues: updates.previousValues ? JSON.parse(JSON.stringify(updates.previousValues)) : selectedEvent.previousValues
      };
    }
    
    const updatedCurrentVersion = updatedVersions.find((v: any) => v.id === currentVersion.id) || currentVersion;
    
    // Update timestamp AFTER successful Firebase operation
    const { updateLastLocalUpdate } = (get() as any);
    if (updateLastLocalUpdate) updateLastLocalUpdate();
    
    set({
      apps: updatedApps,
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion,
      currentScreen: updatedCurrentScreen,
      selectedEvent: updatedSelectedEvent
    });
    
    console.log('[eventsSlice.updateEvent] ✅ Local state updated. Event:', eventId, 'Updates:', Object.keys(updates));
    
    // Add history record - only store relevant fields for history display
    const relevantOldValue = event ? {
      name: event.name,
      type: event.type,
      description: event.description,
      status: event.status,
      params: event.params
    } : undefined;
    
    const relevantNewValue = {
      name: updates.name,
      type: updates.type,
      description: updates.description,
      status: updates.status,
      params: updates.params
    };
    
    (get() as any).addHistoryRecord({
      projectId: currentApp.id,
      action: 'update',
      entityType: 'event',
      entityId: eventId,
      entityName: event?.name || 'unknown',
      userId: user.id,
      userName: user.displayName || user.name,
      oldValue: relevantOldValue,
      newValue: relevantNewValue,
      description: `Оновлено подію "${event?.name || 'unknown'}" на екрані "${currentScreen?.displayName || currentScreen?.name || 'unknown'}"`
    });
    
    // Сохраняем в Firestore
    try {
      const result = await updateEventInFirestore(
        currentApp.id,
        currentVersion.id,
        screenId,
        eventId,
        updates,
        user,
        event
      );
      
      if (!result) {
        // Rollback при ошибке
        set({
          apps: previousApps,
          currentApp: previousCurrentApp,
          currentVersion: previousCurrentVersion,
          currentScreen: previousCurrentScreen,
          selectedEvent: previousSelectedEvent
        });
        console.error('Failed to update event in Firestore');
      }
    } catch (error) {
      // Rollback при ошибке
      set({
        apps: previousApps,
        currentApp: previousCurrentApp,
        currentVersion: previousCurrentVersion,
        currentScreen: previousCurrentScreen,
        selectedEvent: previousSelectedEvent
      });
      console.error('Error updating event in Firestore:', error);
    }
  },
  
  // Delete event
  deleteEvent: async (screenId: string, eventId: string) => {
    const { apps, currentApp, currentVersion, currentScreen, selectedEvent } = (get() as any);
    if (!currentApp || !currentVersion) return;
    
    const user = (get() as any).user;
    if (!user) return;
    
    const deletedEvent = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
    
    // Сохраняем предыдущее состояние для rollback
    const previousApps = apps;
    const previousCurrentApp = currentApp;
    const previousCurrentVersion = currentVersion;
    const previousCurrentScreen = currentScreen;
    const previousSelectedEvent = selectedEvent;
    
    const updatedVersions = (currentApp.versions || []).map((v: any) => {
      if (v.id === currentVersion.id) {
        return {
          ...v,
          screens: (v.screens || []).map((s: any) => {
            if (s.id === screenId) {
              return {
                ...s,
                events: (s.events || [])
                  .filter((e: AnalyticsEvent) => e.id !== eventId)
                  .map((e: AnalyticsEvent) => deepCloneEvent(e))
              };
            }
            return s;
          })
        };
      }
      return v;
    });
    
    const updatedApps = apps.map((app: any) => {
      if (app.id === currentApp.id) {
        return {
          ...app,
          versions: updatedVersions,
          updatedAt: new Date().toISOString()
        };
      }
      return app;
    });
    
    let updatedCurrentScreen = currentScreen;
    if (currentScreen?.id === screenId) {
      updatedCurrentScreen = {
        ...currentScreen,
        events: (currentScreen.events || []).filter((e: AnalyticsEvent) => e.id !== eventId)
      };
    }
    
    const updatedCurrentVersion = updatedVersions.find((v: any) => v.id === currentVersion.id) || currentVersion;
    
    // Update timestamp AFTER successful Firebase operation
    const { updateLastLocalUpdate } = (get() as any);
    if (updateLastLocalUpdate) updateLastLocalUpdate();
    
    set({
      apps: updatedApps,
      currentApp: { ...currentApp, versions: updatedVersions },
      currentVersion: updatedCurrentVersion,
      currentScreen: updatedCurrentScreen,
      selectedEvent: selectedEvent?.id === eventId ? null : selectedEvent
    });
    
    // Add history record - only store relevant fields for history display
    const relevantOldValue = deletedEvent ? {
      name: deletedEvent.name,
      type: deletedEvent.type,
      description: deletedEvent.description,
      status: deletedEvent.status,
      params: deletedEvent.params
    } : undefined;
    
    (get() as any).addHistoryRecord({
      projectId: currentApp.id,
      action: 'delete',
      entityType: 'event',
      entityId: eventId,
      entityName: deletedEvent?.name || 'unknown',
      userId: user.id,
      userName: user.displayName || user.name,
      oldValue: relevantOldValue,
      description: `Видалено подію "${deletedEvent?.name || 'unknown'}" з екрану "${currentScreen?.displayName || currentScreen?.name || 'unknown'}"`
    });
    
    // Сохраняем в Firestore
    try {
      const result = await deleteEventInFirestore(
        currentApp.id,
        currentVersion.id,
        screenId,
        eventId,
        user
      );
      
      if (!result) {
        // Rollback при ошибке
        set({
          apps: previousApps,
          currentApp: previousCurrentApp,
          currentVersion: previousCurrentVersion,
          currentScreen: previousCurrentScreen,
          selectedEvent: previousSelectedEvent
        });
        console.error('Failed to delete event in Firestore');
      }
    } catch (error) {
      // Rollback при ошибке
      set({
        apps: previousApps,
        currentApp: previousCurrentApp,
        currentVersion: previousCurrentVersion,
        currentScreen: previousCurrentScreen,
        selectedEvent: previousSelectedEvent
      });
      console.error('Error deleting event in Firestore:', error);
    }
  },
  
  // Update event position
  updateEventPosition: (screenId: string, eventId: string, x: number, y: number) => {
    get().updateEvent(screenId, eventId, { x, y });
  },
  
  // Select event
  selectEvent: (eventId: string | null) => {
    const { currentScreen, selectedEvent } = (get() as any);
    
    if (!eventId) {
      set({ selectedEvent: null });
      return;
    }
    
    if (selectedEvent?.id === eventId) {
      set({ selectedEvent: null });
      return;
    }
    
    if (!currentScreen) {
      set({ selectedEvent: null });
      return;
    }
    
    const event = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
    set({ selectedEvent: event || null });
  },
  
  // Clear selection
  clearSelection: () => {
    set({ selectedEvent: null });
  },
  
  // Toggle event implemented
  toggleEventImplemented: (screenId: string, eventId: string) => {
    const { currentScreen, user } = (get() as any);
    if (!user || user.role !== 'developer' || !currentScreen) return;
    
    const event = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
    if (!event) return;
    const newImplemented = !event.implemented;
    
    get().updateEvent(screenId, eventId, { implemented: newImplemented });
  },
  
  // Set event status
  setEventStatus: (screenId: string, eventId: string, status: EventStatus) => {
    const { currentScreen, currentApp, user } = (get() as any);
    if (!user || !currentScreen) return false;
    
    const event = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
    
    // Get effective role considering ownership (own vs shared)
    const effectiveRole = currentApp ? permissionService.getEffectiveRole(user, currentApp).role : user.role;
    
    if (effectiveRole === 'analyst') {
      get().updateEvent(screenId, eventId, { status, lastStatusChangedBy: 'analyst' });
      return true;
    }
    
    if (effectiveRole === 'developer') {
      if (!event) return false;
      
      const currentStatus = event.status;
      
      // For 'implemented' and 'deleted' statuses, only allow change if analyst didn't set them
      // Allow if set by developer or if lastStatusChangedBy is undefined (backward compatibility)
      if ((currentStatus === 'implemented' || currentStatus === 'deleted') && 
          event.lastStatusChangedBy === 'analyst') {
        return false;
      }
      
      const allowedTransitions: Record<string, string[]> = {
        'new': ['implemented'],
        'implemented': ['new', 'edited'],
        'edited': ['implemented'],
        'pending_deletion': ['deleted'],
        'deleted': ['pending_deletion']
      };
      
      const allowed = allowedTransitions[currentStatus] || [];
      if (allowed.includes(status)) {
        const updates: Partial<AnalyticsEvent> & { previousStatus?: string | null } = { 
          status, 
          lastStatusChangedBy: 'developer' 
        };
        
        if (status === 'implemented' || status === 'deleted') {
          // Store the previous status to allow reverting
          updates.previousStatus = currentStatus;
        } else if ((status === 'new' || status === 'edited' || status === 'pending_deletion') &&
                   (currentStatus === 'implemented' || currentStatus === 'deleted')) {
          // Clear previousStatus when reverting - use null instead of undefined for Firestore
          updates.previousStatus = null;
        }
        
        get().updateEvent(screenId, eventId, updates);
        return true;
      }
    }
    return false;
  },
  
  // Confirm changes
  confirmChanges: (screenId: string, eventId: string) => {
    const { currentScreen, currentApp, user } = (get() as any);
    if (!user || user.role !== 'developer' || !currentScreen) return false;
    
    const event = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
    if (!event || event.status !== 'edited') return false;
    
    get().updateEvent(screenId, eventId, {
      status: 'implemented',
      previousValues: undefined,
      lastStatusChangedBy: 'developer'
    });
    
    if (currentApp) {
      (get() as any).addHistoryRecord({
        projectId: currentApp.id,
        action: 'update',
        entityType: 'event',
        entityId: eventId,
        entityName: event.name,
        userId: user.id,
        userName: user.displayName || user.name,
        oldValue: { status: 'edited' },
        newValue: { status: 'implemented' },
        description: `Підтверджено зміни події "${event.name}"`
      });
    }
    
    return true;
  },
  
  // Add comment - available to all roles (analyst, developer, viewer)
  // When adding a comment, it is automatically marked as read for the author
  addComment: (screenId: string, eventId: string, text: string) => {
    const { apps, currentApp, currentVersion, currentScreen, selectedEvent, user } = (get() as any);
    if (!currentApp || !currentVersion || !user) return;
    
    // Get effective role in project (considering ownership)
    const { role: projectRole } = permissionService.getEffectiveRole(user, currentApp);
    
    const newComment: Comment = {
      id: generateId(),
      text,
      author: user.displayName || user.name || user.username || 'Unknown',
      authorId: user.id,
      authorRole: user.role, // Global role
      projectRole: projectRole, // Role in this project (analyst/developer/viewer)
      createdAt: new Date().toISOString()
    };
    
    get().updateEvent(screenId, eventId, {
      comments: [...(currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId)?.comments || []), newComment]
    });
    
    // Mark the new comment as read for the author (self)
    // This runs asynchronously without blocking
    markCommentAsRead(
      user.id,
      currentApp.id,
      screenId,
      eventId,
      newComment.id
    ).catch(err => console.error('[eventsSlice.addComment] Failed to mark own comment as read:', err));
    
    // Add history record
    if (currentApp) {
      const event = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
      (get() as any).addHistoryRecord({
        projectId: currentApp.id,
        action: 'comment',
        entityType: 'event',
        entityId: eventId,
        entityName: event?.name || 'unknown',
        userId: user.id,
        userName: user.displayName || user.name,
        newValue: { comment: text },
        description: `Додано коментар до події "${event?.name || 'unknown'}"`
      });
    }
  },
  
  // Delete comment
  deleteComment: (screenId: string, eventId: string, commentId: string) => {
    const { currentScreen } = (get() as any);
    if (!currentScreen) return;
    
    const event = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
    if (!event) return;
    
    const updatedComments = event.comments.filter((c: Comment) => c.id !== commentId);
    get().updateEvent(screenId, eventId, { comments: updatedComments });
  },
  
  // Mark comments as read
  markCommentsAsRead: (screenId: string, eventId: string) => {
    const { currentScreen } = (get() as any);
    if (!currentScreen) return;
    
    const event = currentScreen?.events?.find((e: AnalyticsEvent) => e.id === eventId);
    if (!event) return;
    
    get().updateEvent(screenId, eventId, { commentsReadAt: new Date().toISOString() });
  }
});
